import {
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  SimpleGrid,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import IconPlusLarge from 'central-icons/IconPlusLarge';
import { useState } from 'react';

import { Button as UiButton } from '@revelio/ui';

import { AlertDialog } from '../../../../../shared/components/alert-dialog';
import { CardListSelect } from '../../../../../shared/ui/card-list';
import { FilterId } from '../../../filters/types/filters';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import { EditParameterModal } from './EditParameterModal';
import styles from './ParameterEditor.module.css';
import { determinePrimaryParameter, toReadableFilterName } from './parameters';

export function ParameterEditor({
  parameters,
  onChange,
}: {
  parameters: ReportParameterConfig[];
  onChange: (next: ReportParameterConfig[]) => void;
}) {
  const usedFilterIds = new Set(parameters.map((p) => p.filterId));
  const changeParameters = (next: ReportParameterConfig[]) => {
    onChange(determinePrimaryParameter(next));
  };

  const allFilterIds = Object.values(FilterId);

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const [editMode, setEditMode] = useState<'add' | 'edit'>('add');
  const [draftParam, setDraftParam] = useState<ReportParameterConfig | null>(
    null
  );
  const [pendingDelete, setPendingDelete] =
    useState<ReportParameterConfig | null>(null);

  const openAdd = () => {
    // pick first available filterId not used yet
    const available = allFilterIds.find((f) => !usedFilterIds.has(f));
    if (!available) return;
    const readable = toReadableFilterName(available);
    const parameter: ReportParameterConfig = {
      parameterId: crypto.randomUUID(),
      filterId: available,
      isPrimary: false,
      name: readable,
      selectionLimit: 1,
    };
    setEditMode('add');
    setDraftParam(parameter);
    onEditOpen();
  };

  const openEdit = (param: ReportParameterConfig) => {
    setEditMode('edit');
    setDraftParam(param);
    onEditOpen();
  };

  const updateParameter = (
    parameterId: string,
    updates: Partial<ReportParameterConfig>
  ) => {
    changeParameters(
      parameters.map((p) =>
        p.parameterId === parameterId ? { ...p, ...updates } : p
      )
    );
  };

  const removeParameter = (parameterId: string) => {
    changeParameters(parameters.filter((p) => p.parameterId !== parameterId));
  };

  return (
    <>
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between">
          <Box fontWeight="semibold" color="text.primary">
            Parameters
          </Box>
          <Tooltip label="Add Parameter">
            <UiButton
              size="sm"
              color="primary"
              variant="ghost"
              onClick={openAdd}
            >
              Add Parameter
            </UiButton>
          </Tooltip>
        </HStack>
        <SimpleGrid spacing="10px" columns={2} gridAutoFlow="row">
          {parameters.map((p) => (
            <CardListSelect<string>
              key={p.parameterId}
              id={p.parameterId}
              label={p.name}
              detail=" "
              variant="report"
              hideCheckbox={true}
              onChange={() => openEdit(p)}
              cta={
                <ParamItemMenu>
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(p);
                    }}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    color="red"
                    _focus={{ bg: 'red.100' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDelete(p);
                    }}
                  >
                    Delete
                  </MenuItem>
                </ParamItemMenu>
              }
            />
          ))}
          <UiButton
            fullWidth
            variant="outlined"
            className={styles.addCard}
            leftIcon={<IconPlusLarge size={16} />}
            onClick={openAdd}
          >
            Add
          </UiButton>
        </SimpleGrid>
      </VStack>

      {draftParam && (
        <EditParameterModal
          key={draftParam.parameterId}
          isOpen={isEditOpen}
          onClose={onEditClose}
          mode={editMode}
          initial={draftParam}
          onSubmit={(param) => {
            if (editMode === 'add') {
              changeParameters([...parameters, param]);
            } else {
              updateParameter(param.parameterId, {
                name: param.name,
                filterId: param.filterId,
                isPrimary: param.isPrimary,
                isOptional: param.isOptional,
                selectionLimit:
                  param.filterId === FilterId.DATE_RANGE
                    ? undefined
                    : param.selectionLimit,
              });
            }
          }}
        />
      )}

      {pendingDelete && (
        <AlertDialog
          isOpen={true}
          alertType="confirm"
          skipDefaultDisclosure={true}
          chakraAlertProps={{ isCentered: true }}
          onClose={() => setPendingDelete(null)}
          headerText="Remove Parameter?"
          bodyContent={
            <span>Are you sure you want to remove {pendingDelete?.name}? </span>
          }
          footerAcceptIsDestructive
          footerAcceptText="Remove"
          onAccept={() => {
            removeParameter(pendingDelete?.parameterId ?? '');
            setPendingDelete(null);
          }}
        />
      )}
    </>
  );
}

const ParamItemMenu = ({ children }: { children: React.ReactNode }) => {
  return (
    <Menu placement="bottom-end" offset={[0, 0]}>
      <MenuButton
        as={IconButton}
        icon={<IconDotGrid1x3Vertical size={14} />}
        size="sm"
        aria-label="Actions"
        variant="ghost"
        color="green.600"
        bg="green.100"
        visibility="hidden"
        _active={{
          color: 'green.600',
          bg: 'green.100',
        }}
        _groupHover={{
          visibility: 'visible',
          color: 'green.600',
          bg: 'green.100',
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
      <Portal>
        <MenuList>{children}</MenuList>
      </Portal>
    </Menu>
  );
};
